import Highcharts from 'highcharts/highstock'
import inRange from 'lodash/inRange'
import isEmpty from 'lodash/isEmpty'

import { SENTIMENTS } from 'features/unifiedFlow/contentPage/generate/constants'

import styles from './SentimentAnalysisChart.module.css'

export interface SentimentAnalysisChartVariables {
  selectedSentiment?: string
  series: { name: string; value: number }[]
  onClickSentiment: ({
    name,
    value,
  }: {
    name: string
    value: string | number
  }) => void
}

const START_ANGLE = -77

const CHART_COLORS_MAPPER = {
  [SENTIMENTS.IMPRESSED]: styles.impressed,
  [SENTIMENTS.HELPFUL]: styles.helpful,
  [SENTIMENTS.CURIOUS]: styles.curious,
  [SENTIMENTS.EXCITED]: styles.excited,
  [SENTIMENTS.SURPRISING]: styles.surprising,
  [SENTIMENTS.APPRECIATIVE]: styles.appreciative,
  [SENTIMENTS.URGENT]: styles.urgent,
}

const CATEGORIES = [
  SENTIMENTS.URGENT,
  SENTIMENTS.IMPRESSED,
  SENTIMENTS.HELPFUL,
  SENTIMENTS.CURIOUS,
  SENTIMENTS.EXCITED,
  SENTIMENTS.SURPRISING,
  SENTIMENTS.APPRECIATIVE,
]

const desiredOrder = [
  SENTIMENTS.IMPRESSED,
  SENTIMENTS.HELPFUL,
  SENTIMENTS.CURIOUS,
  SENTIMENTS.EXCITED,
  SENTIMENTS.SURPRISING,
  SENTIMENTS.APPRECIATIVE,
  SENTIMENTS.URGENT,
]

function formatLabels(
  value: string | number,
  selectedSentiment: string | undefined
) {
  return `<span class=${
    value === selectedSentiment ? 'font-bold' : ''
  }>${value}</span>`
}

export function getSentimentAnalysisChartOptions({
  series,
  onClickSentiment,
  selectedSentiment,
}: SentimentAnalysisChartVariables) {
  const orderedSeries = desiredOrder
    .map((sentiment) => series.find((item) => item.name === sentiment))
    .filter(Boolean) as SentimentAnalysisChartVariables['series']

  const chartOptions: Highcharts.Options = {
    lang: {
      noData: `<div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.00004 4V9.33333" stroke="#4B5563"/>
          <path d="M8.00004 10.6666V12" stroke="#4B5563"/>
          <path d="M0.666664 7.99996C0.666664 9.94489 1.43928 11.8102 2.81455 13.1854C4.18982 14.5607 6.05508 15.3333 8 15.3333C9.94493 15.3333 11.8102 14.5607 13.1855 13.1854C14.5607 11.8102 15.3333 9.94489 15.3333 7.99996C15.3333 6.05504 14.5607 4.18978 13.1855 2.81451C11.8102 1.43925 9.94493 0.666626 8 0.666626C6.05508 0.666626 4.18982 1.43925 2.81455 2.81451C1.43928 4.18978 0.666664 6.05504 0.666664 7.99996Z" stroke="#4B5563"/>
        </svg>
        No info available
      </div>`,
    },
    noData: {
      useHTML: true,
      style: {
        fontSize: '14px',
        color: '#605030',
        width: 160,
        textAlign: 'center',
        fontWeight: '400',
      },
    },
    chart: {
      width: 326,
      height: 326,
      backgroundColor: 'transparent',
      polar: true,
      events: {
        render: function () {
          const ticks = this.xAxis[0].ticks
          const length = this.xAxis[0].categories.length
          let rotation = START_ANGLE

          for (const tick in ticks) {
            if (
              rotation &&
              (inRange(rotation, START_ANGLE, 90) || rotation >= 270)
            ) {
              ticks[tick].label?.attr({
                rotation: rotation,
              })
            } else {
              ticks[tick].label?.attr({
                rotation: (rotation + 180) % 360,
              })
            }

            rotation = rotation + 360 / length
          }
        },
      },
    },
    exporting: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    xAxis: {
      tickInterval: 1,
      min: 0,
      max: CATEGORIES.length,
      tickmarkPlacement: 'between',
      categories: CATEGORIES,
      showEmpty: false,
      lineWidth: 1,
      lineColor: '#A89163',
      gridLineColor: 'transparent',
      labels: {
        align: 'center',
        useHTML: true,
        formatter: function ({ value }) {
          return formatLabels(value, selectedSentiment)
        },
        style: {
          color: '#605030',
          fontSize: '12px',
          textTransform: 'capitalize',
        },
      },
    },
    pane: {
      startAngle: START_ANGLE,
      background: [
        {
          backgroundColor: isEmpty(series) ? '#FBF7F0' : 'transparent',
          borderWidth: 0,
        },
      ],
    },
    yAxis: {
      gridLineColor: '#A89163',
      angle: -START_ANGLE,
      min: 0,
      lineWidth: 0,
      tickInterval: 1,
      labels: {
        enabled: false,
      },
    },
    credits: {
      enabled: false,
    },
    title: undefined,
    plotOptions: {
      series: {
        pointInterval: 1,
        borderWidth: 1,
        pointPlacement: 'between',
        cursor: 'pointer',
        point: {
          events: {
            click: function () {
              onClickSentiment({ name: this.name, value: this.category })
            },
          },
        },
      },
      column: {
        pointPadding: 0,
        groupPadding: 0,
      },
    },
    series: [
      {
        type: 'column',
        data: orderedSeries.map(({ name, value }, index: number) => {
          const className = CHART_COLORS_MAPPER[name]

          return {
            x: index + 0.5,
            y: value,
            name: name,
            className,
          }
        }),
      },
    ],
  }

  return chartOptions
}
